/**
 * Inactivity logout (one tracker for the whole app).
 *
 * A signed-in user is logged out after a stretch with no activity:
 *   - 1 hour by default (players with no link, pending or unverified links, casino
 *     staff accounts, Emulate sessions);
 *   - 4 hours when the user has ANY linkedAccounts entry, at any casino, whose
 *     verification is 'verified'.
 * The limit is picked at login from the Login response's user.linkedAccounts and can
 * only be raised during the session (GetMe on casino entry shows a new verified link).
 *
 * A warning shows 5 minutes before logout (a quarter of the limit when the limit is
 * shorter, e.g. the dev-only debug override) with the time left.
 *
 * lastActivityAt and the limit live in localStorage so the check survives a relaunch,
 * backgrounding and a locked phone, and is shared by every tab. Logout (/logout ->
 * clearState) wipes localStorage, which ends the session record too.
 *
 * Everything below the storage helpers is pure and takes `now` explicitly so it can be
 * unit tested without timers.
 */

export const MINUTE_MS = 60 * 1000;
export const SESSION_LIMIT_DEFAULT_MS = 60 * MINUTE_MS; // 1 hour
export const SESSION_LIMIT_VERIFIED_MS = 4 * 60 * MINUTE_MS; // 4 hours
export const SESSION_WARNING_MS = 5 * MINUTE_MS;
/** Activity is recorded at most this often (localStorage write throttle). */
export const ACTIVITY_WRITE_THROTTLE_MS = 10 * 1000;
/** Shortest limit the dev-only debug override accepts. */
export const DEBUG_LIMIT_MIN_MS = 30 * 1000;

export const LAST_ACTIVITY_KEY = 'sessionLastActivityAt';
export const LIMIT_KEY = 'sessionInactivityLimitMs';
export const DEBUG_LIMIT_KEY = 'sessionDebugLimitMs';
/** Dev builds only: ?sessionDebugLimit=<seconds> shortens the limit until logout. */
export const DEBUG_LIMIT_QUERY_PARAM = 'sessionDebugLimit';

export type SessionPhase = 'active' | 'warning' | 'expired';

export interface SessionRecord {
  lastActivityAt: number;
  limitMs: number;
}

export interface SessionStatus {
  phase: SessionPhase;
  /** Time until logout (0 once expired). */
  remainingMs: number;
  /** When the warning starts / started (epoch ms). */
  warnAt: number;
  /** When logout happens (epoch ms). */
  expiresAt: number;
}

// ---------------------------------------------------------------------------
// Pure rules
// ---------------------------------------------------------------------------

/** True when any linked account (any casino) has verification === 'verified'. */
export function hasVerifiedPlayerLink(linkedAccounts: unknown): boolean {
  if (!Array.isArray(linkedAccounts)) return false;
  return linkedAccounts.some((account) => {
    if (!account || typeof account !== 'object') return false;
    const verification = (account as { verification?: unknown }).verification;
    return typeof verification === 'string' && verification.trim().toLowerCase() === 'verified';
  });
}

/**
 * 4 hours with a verified link at any casino, otherwise 1 hour. Pending, unverified,
 * no links and casino staff accounts (no verified player link) all get 1 hour.
 */
export function selectInactivityLimitMs(linkedAccounts: unknown): number {
  return hasVerifiedPlayerLink(linkedAccounts)
    ? SESSION_LIMIT_VERIFIED_MS
    : SESSION_LIMIT_DEFAULT_MS;
}

/** Warning lead: 5 minutes, or a quarter of the limit when the limit is shorter. */
export function warningLeadMs(limitMs: number): number {
  if (!Number.isFinite(limitMs) || limitMs <= 0) return 0;
  return Math.min(SESSION_WARNING_MS, Math.floor(limitMs / 4));
}

export function isValidLimit(limitMs: unknown): limitMs is number {
  return typeof limitMs === 'number' && Number.isFinite(limitMs) && limitMs > 0;
}

export function getSessionStatus(now: number, record: SessionRecord): SessionStatus {
  const expiresAt = record.lastActivityAt + record.limitMs;
  const warnAt = expiresAt - warningLeadMs(record.limitMs);
  const remainingMs = Math.max(0, expiresAt - now);
  let phase: SessionPhase = 'active';
  if (now >= expiresAt) phase = 'expired';
  else if (now >= warnAt) phase = 'warning';
  return { phase, remainingMs, warnAt, expiresAt };
}

/** Raising only: a verified link can lift 1h to 4h, nothing lowers a session. */
export function raisedLimitMs(currentLimitMs: number, linkedAccounts: unknown): number {
  const next = selectInactivityLimitMs(linkedAccounts);
  return next > currentLimitMs ? next : currentLimitMs;
}

/** Throttle: write when nothing is stored yet or the last write is old enough. */
export function shouldWriteActivity(now: number, storedLastActivityAt: number | null): boolean {
  if (storedLastActivityAt == null || !Number.isFinite(storedLastActivityAt)) return true;
  return now - storedLastActivityAt >= ACTIVITY_WRITE_THROTTLE_MS;
}

/** "4:59", "0:07", "12:00". Rounds up so the countdown never shows 0:00 early. */
export function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
}

/** Dev builds are every host type except 'prod' (config/config.ts uses the dev pool). */
export function isDebugOverrideAllowed(hostType: string | undefined): boolean {
  return hostType !== 'prod';
}

/** Parses the debug override (seconds in the URL, ms in storage), or null. */
export function parseDebugLimitSeconds(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.max(DEBUG_LIMIT_MIN_MS, Math.round(seconds * 1000));
}

// ---------------------------------------------------------------------------
// Storage (localStorage, shared by every tab; wiped by clearState on logout)
// ---------------------------------------------------------------------------

function readNumber(key: string): number | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null || raw === '') return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function writeNumber(key: string, value: number): void {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* storage unavailable: the in-memory timer still runs for this page */
  }
}

function hostType(): string | undefined {
  try {
    return process.env.REACT_APP_HOST_TYPE;
  } catch {
    return undefined;
  }
}

/** Dev builds only: the shortened limit set with ?sessionDebugLimit=<seconds>. */
export function readDebugLimitMs(): number | null {
  if (!isDebugOverrideAllowed(hostType())) return null;
  const value = readNumber(DEBUG_LIMIT_KEY);
  return value != null && value >= DEBUG_LIMIT_MIN_MS ? value : null;
}

/** Dev builds only: picks ?sessionDebugLimit=<seconds> off the URL into storage. */
export function captureDebugLimitFromUrl(search: string): void {
  if (!isDebugOverrideAllowed(hostType())) return;
  try {
    const value = new URLSearchParams(search).get(DEBUG_LIMIT_QUERY_PARAM);
    if (value == null) return;
    const ms = parseDebugLimitSeconds(value);
    if (ms == null) window.localStorage.removeItem(DEBUG_LIMIT_KEY);
    else writeNumber(DEBUG_LIMIT_KEY, ms);
  } catch {
    /* ignore */
  }
}

/** The stored session, or null when there is none (signed out / not started). */
export function readSession(): SessionRecord | null {
  const lastActivityAt = readNumber(LAST_ACTIVITY_KEY);
  const storedLimit = readNumber(LIMIT_KEY);
  if (lastActivityAt == null || !isValidLimit(storedLimit)) return null;
  const debugLimit = readDebugLimitMs();
  return { lastActivityAt, limitMs: debugLimit ?? storedLimit };
}

/** Login: a fresh session with the limit for this user. */
export function startSession(now: number, linkedAccounts: unknown): SessionRecord {
  const limitMs = selectInactivityLimitMs(linkedAccounts);
  writeNumber(LIMIT_KEY, limitMs);
  writeNumber(LAST_ACTIVITY_KEY, now);
  return { lastActivityAt: now, limitMs };
}

/** For a session signed in before this tracker existed: start it with 1 hour. */
export function ensureSession(now: number): SessionRecord {
  return readSession() ?? startSession(now, null);
}

/** GetMe: lift to 4h when a verified link shows up; never lowers. */
export function raiseSessionLimit(linkedAccounts: unknown): void {
  const storedLimit = readNumber(LIMIT_KEY);
  if (!isValidLimit(storedLimit)) return; // no session to raise
  const next = raisedLimitMs(storedLimit, linkedAccounts);
  if (next !== storedLimit) writeNumber(LIMIT_KEY, next);
}

/**
 * User activity. Throttled; `force` writes now ('Stay logged in'). Returns false when
 * the session had already expired, in which case nothing is recorded (the caller logs
 * out: activity after the limit never revives a session).
 */
export function recordActivity(now: number, force = false): boolean {
  const record = readSession();
  if (!record) return true;
  if (getSessionStatus(now, record).phase === 'expired') return false;
  if (force || shouldWriteActivity(now, record.lastActivityAt)) {
    writeNumber(LAST_ACTIVITY_KEY, now);
  }
  return true;
}

/** True only when a session exists and its limit has passed. */
export function isStoredSessionExpired(now: number): boolean {
  const record = readSession();
  return Boolean(record) && getSessionStatus(now, record as SessionRecord).phase === 'expired';
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(LAST_ACTIVITY_KEY);
    window.localStorage.removeItem(LIMIT_KEY);
  } catch {
    /* ignore */
  }
}

/** Full logout through the existing route (Auth.signOut + clearState). */
let logoutRequested = false;
export function requestInactivityLogout(): void {
  if (logoutRequested) return;
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/logout') return;
  logoutRequested = true;
  window.location.replace('/logout');
}
